// JOBE-0164.5: RecentCorrectionsFeed — recent correction events feed on W6 Fleet.
//
// Layout: two-column CSS grid (.recent-events-grid).
//   LEFT column  — this component; correction events from fleet.recentCorrections.
//   RIGHT column — forget-feed placeholder (JOBE-0167.6 sibling, stub until W8 lands).
//
// Props:
//   fleet  — the promptops.mock.fleet fixture object; corrections sourced from
//             fleet.recentCorrections.
//   now    — optional Date used for relative-time calculation (defaults to Date.now()).
//             Injected by tests for deterministic output.

import './RecentCorrectionsFeed.css';

// ---- slugify (shared with FailureClassIndex) ----
// Same rule as FailureClassIndex: lower, non-alnum → hyphen, strip leading/trailing hyphens.
export function slugify(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// ---- formatRelativeTime ----
// Returns { relative, absolute } for an ISO timestamp.
//   relative  — "Xh ago" / "Xd ago" / "" for > 30 days.
//   absolute  — "D Mon" (e.g. "22 May"), always present for valid dates.
// `now` param defaults to new Date() so tests can inject a fixed reference.
export function formatRelativeTime(iso, now) {
  if (!iso) return { relative: '', absolute: '' };
  const date = new Date(iso);
  if (isNaN(date.getTime())) return { relative: '', absolute: '' };
  const ref = now instanceof Date ? now : new Date();
  const ms = ref.getTime() - date.getTime();
  const s = Math.floor(ms / 1000);

  const absolute = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

  if (s < 0) return { relative: '', absolute };

  const THIRTY_DAYS_S = 30 * 24 * 3600;
  if (s >= THIRTY_DAYS_S) return { relative: '', absolute };

  let relative;
  if (s < 60)     relative = `${s}s ago`;
  else if (s < 3600)  relative = `${Math.floor(s / 60)}m ago`;
  else if (s < 86400) relative = `${Math.floor(s / 3600)}h ago`;
  else                relative = `${Math.floor(s / 86400)}d ago`;

  return { relative, absolute };
}

function titleCase(s) {
  if (!s) return '';
  return String(s).replace(/(\S+)/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

// ---- CorrectionsFeed ----

function CorrectionsFeed({ corrections, now }) {
  const sorted = [...corrections].sort((a, b) => {
    const ta = a.ts ? new Date(a.ts).getTime() : 0;
    const tb = b.ts ? new Date(b.ts).getTime() : 0;
    return tb - ta; // newest first
  });

  if (sorted.length === 0) {
    return (
      <section className="corrections-feed">
        <p className="feed-empty">No corrections in the last 30 days.</p>
      </section>
    );
  }

  function handleChipClick(cls) {
    const idx = document.getElementById('failure-class-index');
    if (!idx) return;
    const slug = slugify(cls);
    const row = idx.querySelector(`.failure-class-index__row[data-failure-class-id="${slug}"]`);
    if (!row) {
      console.warn(`[RecentCorrectionsFeed] no failure-class row found for slug "${slug}"`);
      return;
    }
    row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    row.classList.add('flash');
  }

  return (
    <section className="corrections-feed">
      {sorted.map((c, i) => {
        const { relative, absolute } = formatRelativeTime(c.ts, now);
        return (
          <div
            key={`${c.agentName}-${c.ts}-${i}`}
            className="recent-event-row"
          >
            <div className="rer-time">
              <span className="rer-rel">{relative}</span>
              <span className="rer-abs">{absolute}</span>
            </div>
            <div className="rer-body">
              <span className="agent-name">{titleCase(c.agentName || '')}</span>
              {c.cls && (
                // eslint-disable-next-line jsx-a11y/anchor-is-valid
                <button
                  type="button"
                  className="pill-chip"
                  onClick={() => handleChipClick(c.cls)}
                >
                  {c.cls}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </section>
  );
}

// ---- ForgetFeedPlaceholder ----

function ForgetFeedPlaceholder() {
  return (
    <section
      className="forget-feed-placeholder"
      aria-label="Recent forget events (placeholder)"
    >
      <p>Forget-event feed — coming in JOBE-0167.6.</p>
    </section>
  );
}

// ---- RecentCorrectionsFeed ----

export function RecentCorrectionsFeed({ fleet = {}, now }) {
  const corrections = Array.isArray(fleet.recentCorrections) ? fleet.recentCorrections : [];

  return (
    <div id="recent-corrections" className="recent-events-grid">
      <CorrectionsFeed corrections={corrections} now={now} />
      <ForgetFeedPlaceholder />
    </div>
  );
}

export default RecentCorrectionsFeed;
